import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { DynamicFormField } from './formFieldMapper';
import type { ColumnFieldDefinition } from '@/services/tableRowService';
import { addItemToChecklist } from '@/services/checklistItemService';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useToast } from '@/hooks/use-toast';

interface AddChecklistItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  checklistComponentId: string;
}

const CHECKLIST_FIELDS: ColumnFieldDefinition[] = [
  {
    name: 'label',
    label: 'Label',
    type: 'text',
    required: true,
  },
  {
    name: 'priority',
    label: 'Priority',
    type: 'select',
    required: false,
    options: [
      { value: 'critical', label: 'Critical', color: '#EF4444' },
      { value: 'high', label: 'High', color: '#F97316' },
      { value: 'medium', label: 'Medium', color: '#3B82F6' },
      { value: 'low', label: 'Low', color: '#9CA3AF' },
    ],
    defaultValue: 'medium',
  },
  {
    name: 'assignee',
    label: 'Assignee',
    type: 'text',
    required: false,
  },
  {
    name: 'dueDate',
    label: 'Due Date',
    type: 'date',
    required: false,
  },
];

const DEFAULT_VALUES = {
  label: '',
  priority: 'medium',
  assignee: '',
  dueDate: new Date().toISOString().split('T')[0],
};

export function AddChecklistItemModal({
  isOpen,
  onClose,
  checklistComponentId,
}: AddChecklistItemModalProps) {
  const editorStore = useEditorStore();
  const viewerStore = useViewerStore();
  const { toast } = useToast();
  const [checklistName, setChecklistName] = useState<string>('Checklist');

  const form = useForm<Record<string, unknown>>({
    defaultValues: { ...DEFAULT_VALUES },
  });

  useEffect(() => {
    if (isOpen && checklistComponentId) {
      let component = editorStore.getComponent?.(checklistComponentId);
      if (!component) {
        component = viewerStore.getComponentById?.(checklistComponentId);
      }

      if (component) {
        const name =
          (component.properties.title as string) ||
          component.name ||
          'Checklist';
        setChecklistName(name);
      }

      form.reset({
        ...DEFAULT_VALUES,
        dueDate: new Date().toISOString().split('T')[0],
      });
    }
  }, [isOpen, checklistComponentId, editorStore, viewerStore, form]);

  const onSubmit = (data: Record<string, unknown>) => {
    const label = ((data.label as string) || '').trim();
    if (!label) {
      toast({
        title: 'Validation Error',
        description: 'Label is required',
        variant: 'destructive',
      });
      return;
    }

    const success = addItemToChecklist(checklistComponentId, {
      label,
      priority: (data.priority as string) || undefined,
      assignee: ((data.assignee as string) || '').trim() || undefined,
      dueDate: ((data.dueDate as string) || '').trim() || undefined,
    });

    if (success) {
      form.reset();
      onClose();

      setTimeout(() => {
        toast({
          title: 'Success',
          description: 'Item added to checklist',
        });
      }, 100);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to add item to checklist',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Checklist Item</DialogTitle>
          <DialogDescription>
            Add a new item to {checklistName}. Fill in the fields below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 py-4 px-6">
              {CHECKLIST_FIELDS.map((column) => (
                <DynamicFormField
                  key={column.name}
                  column={column}
                  control={form.control}
                  fieldName={column.name as never}
                />
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Adding...' : 'Add Item'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
